export const REDIRECT_ON_LOGIN_STORAGE_KEY = "REDIRECT_ON_LOGIN_STORAGE_KEY";

type AuthRedirectQueryValue = string | string[] | undefined;
type AuthRedirectQuery = Record<string, AuthRedirectQueryValue>;
type AuthRedirectUrl = {
  pathname: string;
  query?: AuthRedirectQuery;
};

export type AuthRedirectEntry = {
  id: string;
  url: AuthRedirectUrl;
};

const isAuthRedirectQueryValue = (
  value: unknown
): value is AuthRedirectQueryValue =>
  typeof value === "string" ||
  value === undefined ||
  (Array.isArray(value) && value.every(item => typeof item === "string"));

const isAuthRedirectQuery = (value: unknown): value is AuthRedirectQuery =>
  typeof value === "object" &&
  value !== null &&
  !Array.isArray(value) &&
  Object.values(value).every(isAuthRedirectQueryValue);

const isAuthRedirectUrl = (value: unknown): value is AuthRedirectUrl => {
  if (typeof value !== "object" || value === null) return false;
  const url = value as Partial<AuthRedirectUrl>;
  return (
    typeof url.pathname === "string" &&
    (url.query === undefined || isAuthRedirectQuery(url.query))
  );
};

const isAuthRedirectEntry = (value: unknown): value is AuthRedirectEntry => {
  if (typeof value !== "object" || value === null) return false;
  const entry = value as Partial<AuthRedirectEntry>;
  return typeof entry.id === "string" && isAuthRedirectUrl(entry.url);
};

export const saveAuthRedirect = (url: AuthRedirectUrl): AuthRedirectEntry => {
  const entry = {
    id: crypto.randomUUID(),
    url
  };
  sessionStorage.setItem(REDIRECT_ON_LOGIN_STORAGE_KEY, JSON.stringify(entry));
  return entry;
};

export const getAuthRedirect = (): AuthRedirectEntry | null => {
  const stored = sessionStorage.getItem(REDIRECT_ON_LOGIN_STORAGE_KEY);
  if (!stored) return null;

  try {
    const parsed: unknown = JSON.parse(stored);
    if (isAuthRedirectEntry(parsed)) {
      return parsed;
    } else if (isAuthRedirectUrl(parsed)) {
      // Read entries written before redirect ownership was added.
      return saveAuthRedirect(parsed);
    }
  } catch {
    // Invalid entries are removed below.
  }

  sessionStorage.removeItem(REDIRECT_ON_LOGIN_STORAGE_KEY);
  return null;
};

export const removeAuthRedirect = (id: string): void => {
  const current = getAuthRedirect();
  if (current?.id === id) {
    sessionStorage.removeItem(REDIRECT_ON_LOGIN_STORAGE_KEY);
  }
};

const queryValueMatches = (
  expected: AuthRedirectQueryValue,
  actual: AuthRedirectQueryValue
): boolean => {
  if (!Array.isArray(expected)) return expected === actual;
  return (
    Array.isArray(actual) &&
    expected.length === actual.length &&
    expected.every((value, index) => value === actual[index])
  );
};

const normalizePathname = (pathname: string): string => {
  let end = pathname.length;
  while (end > 1 && pathname[end - 1] === "/") {
    end -= 1;
  }
  return pathname.slice(0, end);
};

export const removeAuthRedirectAtDestination = (
  pathname: string,
  query: AuthRedirectQuery
): void => {
  const entry = getAuthRedirect();
  if (
    !entry ||
    normalizePathname(entry.url.pathname) !== normalizePathname(pathname)
  ) {
    return;
  }

  const expectedQuery = entry.url.query ?? {};
  const arrived = Object.entries(expectedQuery).every(([key, value]) =>
    queryValueMatches(value, query[key])
  );
  if (arrived) {
    removeAuthRedirect(entry.id);
  }
};
